import './admin.css';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/header';
import CustomToaster from '../components/custom-toaster';
import { authFetch, getDomain } from '../utils/apis/api';

const BASE_URL = getDomain();

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${sessionStorage.getItem('token')}`,
});

const Admin = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');

  const showAlert = (msg, variant = 'success') => {
    setToastMessage(msg);
    setToastVariant(variant);
    setShowToast(true);
  };

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await authFetch(`${BASE_URL}admin/notifications`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      console.log('Fetched notifications:', data);
      setNotifications(data.items || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    const userType = sessionStorage.getItem('userType');
    if (!token || userType !== 'admin') {
      navigate('/');
      return;
    }
    fetchNotifications();
  }, [navigate, fetchNotifications]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!title.trim() || !message.trim()) return;

    setIsSubmitting(true);
    try {
      const res = await authFetch(`${BASE_URL}admin/notifications`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ title: title.trim(), message: message.trim() }),
      });
      if (!res.ok) throw new Error('Failed to create');
      setTitle('');
      setMessage('');
      showAlert('Notification created successfully!', 'success');
      fetchNotifications();
    } catch (err) {
      showAlert('Failed to create notification.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await authFetch(`${BASE_URL}admin/notifications/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Failed to delete');
      showAlert('Notification removed.', 'success');
      fetchNotifications();
    } catch (err) {
      showAlert('Failed to remove notification.', 'error');
    }
  };

  const formatDate = (iso) => {
    const timezone = sessionStorage.getItem('timezone') || undefined;

    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timezone,
    });
  };

  return (
    <>
      <Header />
      <div className="admin-page">
        <div className="admin-container">
          <h2 className="admin-heading">Admin — Notifications</h2>

          {/* Create form */}
          <div className="admin-card">
            <h5 className="admin-card-title">Create Notification</h5>
            <form onSubmit={handleCreate}>
              <input
                className="admin-input"
                type="text"
                placeholder="Title"
                maxLength={255}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
              <textarea
                className="admin-textarea"
                placeholder="Message"
                maxLength={5000}
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
              />
              <button
                className="admin-btn-primary"
                type="submit"
                disabled={isSubmitting || !title.trim() || !message.trim()}
              >
                {isSubmitting ? 'Sending...' : 'Send to all users'}
              </button>
            </form>
          </div>

          {/* Notifications list */}
          <div className="admin-card">
            <h5 className="admin-card-title">
              Sent Notifications
              <span className="admin-badge">{notifications.length}</span>
            </h5>
            {notifications.length === 0 ? (
              <p className="admin-empty">No notifications yet.</p>
            ) : (
              <ul className="admin-notif-list">
                {notifications.map((n) => (
                  <li key={n.id} className={`admin-notif-item${n.is_active ? '' : ' admin-notif-inactive'}`}>
                    <div className="admin-notif-body">
                      <div className="admin-notif-title">
                        {n.title}
                        {!n.is_active && <span className="admin-notif-deleted-tag">Deleted</span>}
                      </div>
                      <div className="admin-notif-message">{n.message}</div>
                      <div className="admin-notif-meta">
                        {formatDate(n.created_at)} &middot; {n.read_count ?? 0}/{n.total_recipients ?? 0} read
                      </div>
                    </div>
                    {n.is_active && (
                      <button
                        className="admin-btn-delete"
                        onClick={() => handleDelete(n.id)}
                        title="Remove notification"
                      >
                        &#x2715;
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {toastMessage && (
        <CustomToaster
          show={showToast}
          onClose={() => setShowToast(false)}
          message={toastMessage}
          type={toastVariant}
        />
      )}
    </>
  );
};

export default Admin;
